/**
 * Putting text on the clipboard, in a shell that denies clipboard permissions.
 *
 * **Why this is not one line.** `navigator.clipboard.writeText` is the obvious
 * call and it *silently does nothing here*. The desktop shell denies every
 * browser permission except local-network-access (`desktop/src/permissions.ts`,
 * deliberately), so Chromium refuses the async Clipboard API's write and the
 * promise rejects with `NotAllowedError`. Every call site then swallowed it —
 * `.catch(() => undefined)` — so the button did nothing, showed no tick, and
 * reported no error. Three passes over its looks did not touch that.
 *
 * `document.execCommand("copy")` is the path that works: it is synchronous,
 * driven by the user's own click, and needs no permission. It is deprecated and
 * still universally implemented, and it is what `bimgo-ai-web`'s own `handleCopy`
 * uses as its **primary** mechanism — the reference reaches for `writeText` only
 * as a last resort. That is the shape copied here, for the same reason.
 *
 * The async API stays as the fallback rather than being dropped: if a future
 * Electron grants the permission, or this runs in a plain browser tab where
 * `execCommand` has finally gone, the fallback is the path that still works.
 *
 * **Returns whether the text actually reached the clipboard**, so a caller can
 * show its confirmation only when there is something to confirm. A tick that
 * appears whether or not the copy happened is worse than no tick: it is the
 * button lying about the one thing it does.
 */

export interface ClipboardHost {
  doc: Document;
  clipboard?: Clipboard | undefined;
}

function defaultHost(): ClipboardHost | null {
  if (typeof document === "undefined") return null;
  return {
    doc: document,
    clipboard: typeof navigator === "undefined" ? undefined : navigator.clipboard,
  };
}

/**
 * The `execCommand` path: stage a hidden carrier, select it, and answer the
 * resulting `copy` event with the real text.
 *
 * The carrier exists because `execCommand("copy")` copies **the selection**, and
 * there may not be one. It is placed off-screen and removed immediately, and the
 * user's own selection is saved and restored around it — copying a message must
 * not clear the text they had highlighted to read.
 */
export function copyViaExecCommand(text: string, doc: Document, html?: string): boolean {
  let written = false;

  const onCopy = (event: ClipboardEvent) => {
    event.preventDefault();
    if (event.clipboardData) {
      event.clipboardData.setData("text/plain", text);
      // The HTML flavour is what lets a mention chip paste back into the
      // editor as a chip (`CustomMention.parseHTML`). It has to travel this
      // way: Chromium's async clipboard sanitiser strips the class the parser
      // keys off, and `execCommand`'s DataTransfer path skips the sanitiser.
      if (html) event.clipboardData.setData("text/html", html);
      written = true;
    }
  };

  doc.addEventListener("copy", onCopy as EventListener, { once: true });

  const carrier = doc.createElement("span");
  // Not empty: a carrier with no content can select as an empty range, and
  // Chromium then declines to fire `copy` at all.
  carrier.textContent = "_";
  carrier.style.cssText =
    "position:fixed;left:-9999px;top:-9999px;opacity:0;pointer-events:none";
  doc.body.appendChild(carrier);

  const selection = doc.defaultView?.getSelection?.() ?? null;
  const saved: Range[] = [];
  try {
    if (selection) {
      for (let i = 0; i < selection.rangeCount; i++) {
        saved.push(selection.getRangeAt(i).cloneRange());
      }
      selection.removeAllRanges();
      const range = doc.createRange();
      range.selectNodeContents(carrier);
      selection.addRange(range);
    }
    doc.execCommand("copy");
  } catch {
    // Fall through: `written` stays false and the caller tries the async API.
  } finally {
    doc.removeEventListener("copy", onCopy as EventListener);
    if (selection) {
      selection.removeAllRanges();
      for (const range of saved) selection.addRange(range);
    }
    carrier.remove();
  }

  return written;
}

/**
 * Copy `text`, and say whether it worked.
 *
 * Never throws: a clipboard failure is a disappointment, not a reason to take
 * the transcript's render down with it.
 */
export async function copyText(text: string, host?: ClipboardHost | null, html?: string): Promise<boolean> {
  const target = host ?? defaultHost();
  if (!target) return false;

  if (copyViaExecCommand(text, target.doc, html)) return true;

  try {
    await target.clipboard?.writeText(text);
    // `writeText` resolving is the only evidence available that it landed; an
    // absent API resolves nothing, so treat that as the failure it is.
    return target.clipboard !== undefined;
  } catch {
    return false;
  }
}
